export const TieredPriceProductId = {
    TieredTileUsage: "mapka_pro_tile_usage",
    TieredStorage: "mapka_pro_storage",
    TieredProcessing: "mapka_pro_processing",
    TieredMapSession: "mapka_pro_map_session",
    TieredInputToken: "mapka_pro_input_token",
    TieredOutputToken: "mapka_pro_output_token",
    TieredAccountUser: "mapka_pro_account_user",
    TieredCustomStyle: "mapka_pro_custom_style",
    TieredCustomTileset: "mapka_pro_custom_tileset",
};
export const TIERED_PRODUCT_IDS = Object.values(TieredPriceProductId);
export const NoAttributionProductId = {
    NoAttribution: "mapka_no_attribution",
};
export const NO_ATTRIBUTION_SUBSCRIPTION_PRODUCT_IDS = Object.values(NoAttributionProductId);
export const SupportProductId = {
    BasicSupport: "mapka_basic_support",
    BusinessSupport: "mapka_business_support",
};
export const SUPPORT_SUBSCRIPTION_PRODUCT_IDS = Object.values(SupportProductId);
export const DataCatalogProductId = {
    DataCatalog: "mapka_data_catalog",
};
export const DATA_CATALOG_SUBSCRIPTION_PRODUCT_IDS = Object.values(DataCatalogProductId);
export const FixedPricesProductId = {
    ...SupportProductId,
    ...NoAttributionProductId,
    ...DataCatalogProductId,
};
export const FIXED_PRICES_PRODUCT_IDS = Object.values(FixedPricesProductId);
/**
 * These would be displayed in invoices.
 * There are also used to defined display units in the UI.
 */
export const UnitLabels = {
    K_REQUESTS: "1K requests",
    GB: "GB",
    MINUTE: "minute",
    MAP: "map",
    TILESET: "tileset",
    TEAM_MEMBER: "team member",
    TOKEN: "token",
};
export const DEV_PARTNER_QUOTA_MULTIPLIER = 40;
/**
 * Pricing structure (all usage-based with free tier):
 *
 * | Feature              | Free Tier           | Price Above Free Tier          |
 * |----------------------|---------------------|--------------------------------|
 * | Monthly Subscription | -                   | $0 / month                     |
 * | Users / Team Seats   | 1 user              | $2 / user                      |
 * | Custom Tilesets      | 1 tileset           | $0.10 / tileset                |
 * | Custom Styles        | 1 style             | $0.10 / style                  |
 * | Map Loads            | 30,000 / month      | $0.30 / 1,000                  |
 * | Storage              | 1 GB                | $0.40 / GB                     |
 * | Tile API Requests    | 200,000             | $0.10 / 1,000                  |
 * | Processing Time      | 100 min             | $0.0035 / min                  |
 * | AI Input Tokens      | 200k                | $7.00 / 1M tokens              |
 * | AI Output Tokens     | 20k                 | $28.00 / 1M tokens             |
 *
 * Add-ons:
 * - No Mapka Attribution: $100 / month
 * - Basic Support: $50 / month
 * - Business Premium Support: $3,000 / year
 */
export const TIERED_PRODUCTS = {
    [TieredPriceProductId.TieredAccountUser]: {
        /**
         * 1 user free
         * $2 per additional user
         */
        freeTierAllowance: 1,
        unitSize: 1,
        unitLabel: UnitLabels.TEAM_MEMBER,
        eventName: "account_user_usage",
        name: "Team members",
        description: "Collaborate with your team. Add unlimited users to your account.",
        price: "200",
    },
    [TieredPriceProductId.TieredCustomTileset]: {
        /**
         * 1 tileset free
         * $0.10 per additional tileset
         */
        freeTierAllowance: 1,
        unitSize: 1,
        unitLabel: UnitLabels.TILESET,
        eventName: "custom_tileset_usage",
        name: "Custom Tilesets",
        description: "Upload and host your own vector or raster tilesets.",
        price: "10",
    },
    [TieredPriceProductId.TieredCustomStyle]: {
        /**
         * 1 style free
         * $0.10 per additional style
         */
        freeTierAllowance: 1,
        unitSize: 1,
        unitLabel: UnitLabels.MAP,
        eventName: "custom_style_usage",
        name: "Custom Map Styles",
        description: "Create unique map styles with our visual editor.",
        price: "10",
    },
    [TieredPriceProductId.TieredMapSession]: {
        /**
         * 30k map loads for free
         * $0.30 per 1,000 map loads
         */
        freeTierAllowance: 30,
        unitSize: 1000,
        unitLabel: UnitLabels.K_REQUESTS,
        eventName: "map_sessions_usage",
        name: "Map Loads",
        description: "Each time a user loads a map, we count it as a map load.",
        price: "30",
    },
    [TieredPriceProductId.TieredStorage]: {
        /**
         * 1GB free quota, we store information in bytes and display in GB
         * $0.40 per GB
         */
        freeTierAllowance: 1,
        unitSize: 1024 * 1024 * 1024,
        unitLabel: UnitLabels.GB,
        eventName: "storage_usage",
        name: "Data Storage",
        description: "Secure cloud storage for your data.",
        price: "40",
    },
    [TieredPriceProductId.TieredTileUsage]: {
        /**
         * 200k tile API requests for free
         * $0.10 per 1,000 requests
         */
        freeTierAllowance: 200,
        unitSize: 1000,
        unitLabel: UnitLabels.K_REQUESTS,
        eventName: "tileset_tile_usage",
        name: "Tile API Requests",
        description: "Serve map tiles via our high-performance global CDN.",
        price: "10",
    },
    [TieredPriceProductId.TieredProcessing]: {
        /**
         * 100 minutes free quota
         * $0.0035 per minute
         */
        freeTierAllowance: 100,
        unitSize: 60,
        unitLabel: UnitLabels.MINUTE,
        eventName: "processing_usage",
        name: "Data Processing",
        description: "Transform and process geospatial data",
        price: "0.35",
    },
    [TieredPriceProductId.TieredInputToken]: {
        /**
         * 200,000 input tokens for free
         * $7.00 per 1M tokens
         */
        freeTierAllowance: 200_000,
        unitSize: 1,
        unitLabel: UnitLabels.TOKEN,
        eventName: "input_token_usage",
        name: "AI Input Tokens",
        description: "Power AI-assisted map generation with natural language prompts.",
        price: "0.0007",
    },
    [TieredPriceProductId.TieredOutputToken]: {
        /**
         * 20k output tokens for free
         * $28.00 per 1M tokens
         */
        freeTierAllowance: 20_000,
        unitSize: 1,
        unitLabel: UnitLabels.TOKEN,
        eventName: "output_token_usage",
        name: "AI Output Tokens",
        description: "Power AI-assisted map generation with natural language prompts.",
        price: "0.0028",
    },
};
/**
 * Fixed price add-ons (monthly/yearly flat fees)
 */
export const FIXED_PRICES = {
    [FixedPricesProductId.NoAttribution]: {
        /**
         * Remove Mapka attribution - $100/month
         */
        amount: 10000,
        interval: "month",
        name: "No Mapka attribution",
        description: "Remove Mapka branding for a fully custom experience.",
    },
    [FixedPricesProductId.BasicSupport]: {
        /**
         * Basic Support - $50/month
         */
        amount: 5000,
        interval: "month",
        name: "Standard Support",
        description: "Priority email support with 24-hour response time.",
    },
    [FixedPricesProductId.BusinessSupport]: {
        /**
         * Business Premium Support - $3,000/year
         */
        amount: 300000,
        interval: "year",
        name: "Enterprise Support",
        description: "Dedicated support with SLA, onboarding assistance, and technical consultation.",
    },
    [FixedPricesProductId.DataCatalog]: {
        /**
         * Data Catalog - $100/month
         */
        amount: 10000,
        interval: "month",
        name: "Data Catalog",
        description: "Access to our data catalog, including vector and raster tilesets, and datasets.",
    },
};
//# sourceMappingURL=prices.js.map